/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

// @mui core components
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';

// Soft UI Dashboard PRO React components
import SoftBox from 'components/SoftBox';
import Breadcrumbs from 'prefabsComponents/Breadcrumbs';
import NotificationItem from 'prefabsComponents/Items/NotificationItem';

// Custom styles for DashboardNavbar
import {
    navbar,
    navbarContainer,
    navbarRow,
    navbarIconButton,
    navbarDesktopMenu,
} from 'prefabsComponents/Navbars/DashboardNavbar/styles';

// Soft UI Dashboard PRO React context
import {
    useSoftUIController,
    setTransparentNavbar,
    setMiniSidenav,
    setOpenConfigurator,
} from 'context';

// Images
import team2 from 'assets/images/logo-brand.png';
import logoSpotify from 'assets/images/small-logos/logo-spotify.svg';

// AccountMenu component import
import AccountMenu from './components/AccountMenu'; // Asegúrate de que la ruta sea correcta

function DashboardNavbar({ absolute, light, isMini }) {
    const [navbarType, setNavbarType] = useState();
    const [controller, dispatch] = useSoftUIController();
    const { miniSidenav, transparentNavbar, fixedNavbar, openConfigurator } =
        controller;
    const [openMenu, setOpenMenu] = useState(false);
    const route = useLocation().pathname.split('/').slice(1);

    useEffect(() => {
        if (fixedNavbar) {
            setNavbarType('sticky');
        } else {
            setNavbarType('static');
        }

        function handleTransparentNavbar() {
            setTransparentNavbar(
                dispatch,
                (fixedNavbar && window.scrollY === 0) || !fixedNavbar
            );
        }

        window.addEventListener('scroll', handleTransparentNavbar);
        handleTransparentNavbar();

        return () =>
            window.removeEventListener('scroll', handleTransparentNavbar);
    }, [dispatch, fixedNavbar]);

    const handleConfiguratorOpen = () =>
        setOpenConfigurator(dispatch, !openConfigurator);
    const handleOpenMenu = event => setOpenMenu(event.currentTarget);
    const handleCloseMenu = () => setOpenMenu(false);

    // Render the notifications menu
    const renderMenu = () => (
        <Menu
            anchorEl={openMenu}
            anchorReference={null}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            open={Boolean(openMenu)}
            onClose={handleCloseMenu}
            sx={{ mt: 2 }}
        >
            <NotificationItem
                image={<img src={team2} alt="person" />}
                title={['New message', 'from Laur']}
                date="13 minutes ago"
                onClick={handleCloseMenu}
            />
            <NotificationItem
                image={<img src={logoSpotify} alt="person" />}
                title={['New album', 'by Travis Scott']}
                date="1 day"
                onClick={handleCloseMenu}
            />
            <NotificationItem
                color="secondary"
                image={
                    <Icon
                        fontSize="small"
                        sx={{ color: ({ palette: { white } }) => white.main }}
                    >
                        payment
                    </Icon>
                }
                title={['', 'Payment successfully completed']}
                date="2 days"
                onClick={handleCloseMenu}
            />
        </Menu>
    );

    return (
        <AppBar
            position={absolute ? 'absolute' : navbarType}
            color="inherit"
            sx={theme => navbar(theme, { transparentNavbar, absolute, light })}
        >
            <Toolbar sx={theme => navbarContainer(theme)}>
                <SoftBox
                    color="inherit"
                    mb={{ xs: 1, md: 0 }}
                    sx={theme => navbarRow(theme, { isMini })}
                >
                    <Breadcrumbs
                        icon="home"
                        title={route[route.length - 1]}
                        route={route}
                        light={light}
                    />

                </SoftBox>
                {isMini ? null : (
                    <SoftBox sx={theme => navbarRow(theme, { isMini })}>
                        <SoftBox color={light ? 'white' : 'inherit'}>
                            <AccountMenu />
                            {renderMenu()}
                        </SoftBox>
                    </SoftBox>
                )}
            </Toolbar>
        </AppBar>
    );
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
    absolute: false,
    light: false,
    isMini: false,
};

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
    absolute: PropTypes.bool,
    light: PropTypes.bool,
    isMini: PropTypes.bool,
};

export default DashboardNavbar;
